import {
  MAX_ACCEPTED_ACCURACY_METERS,
  MAX_ACCEPTED_SPEED_METRES_PER_SECOND,
  ROUTE_TRACE_MAX_POINTS,
  ROUTE_TRACE_MIN_POINT_DISTANCE_METERS,
  ROUTE_TRACE_MIN_POINT_INTERVAL_MS,
  WALK_DURATION_SECONDS,
  WALK_TURN_AROUND_SECONDS,
} from "./sixMinuteWalkConfig";

const WALK_DEBUG_ENABLED = process.env.NODE_ENV === "development";

export function logSixMinuteWalkFlowDebug(level, message, details = undefined) {
  if (!WALK_DEBUG_ENABLED || typeof console === "undefined") {
    return;
  }

  const logger =
    level === "error"
      ? console.error
      : level === "warn"
        ? console.warn
        : console.info;

  if (details === undefined) {
    logger(`[SixMinuteWalkFlow] ${message}`);
    return;
  }

  logger(`[SixMinuteWalkFlow] ${message}`, details);
}

export function formatDurationLabel(totalSeconds) {
  const safeSeconds = Math.max(0, Math.round(totalSeconds));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function formatDistanceLabel(distanceMeters) {
  if (typeof distanceMeters !== "number" || !Number.isFinite(distanceMeters)) {
    return "0";
  }

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: distanceMeters >= 1000 ? 0 : 1,
  }).format(distanceMeters);
}

export function readFiniteNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

export function computeWalkProgressPercent(elapsedSeconds) {
  const boundedSeconds = Math.max(
    0,
    Math.min(WALK_DURATION_SECONDS, Math.floor(elapsedSeconds)),
  );

  if (boundedSeconds <= WALK_TURN_AROUND_SECONDS) {
    return (boundedSeconds / WALK_TURN_AROUND_SECONDS) * 100;
  }

  return Math.max(
    0,
    100 -
      ((boundedSeconds - WALK_TURN_AROUND_SECONDS) / WALK_TURN_AROUND_SECONDS) *
        100,
  );
}

function toRadians(degrees) {
  return (degrees * Math.PI) / 180;
}

export function haversineDistanceMeters(a, b) {
  const lat1 = Number(a?.lat);
  const lon1 = Number(a?.lng);
  const lat2 = Number(b?.lat);
  const lon2 = Number(b?.lng);

  if (
    !Number.isFinite(lat1) ||
    !Number.isFinite(lon1) ||
    !Number.isFinite(lat2) ||
    !Number.isFinite(lon2)
  ) {
    return 0;
  }

  const earthRadiusMeters = 6371e3;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const lat1Radians = toRadians(lat1);
  const lat2Radians = toRadians(lat2);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1Radians) *
      Math.cos(lat2Radians) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  return 2 * earthRadiusMeters * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export function roundTo(value, digits = 1) {
  if (!Number.isFinite(value)) {
    return null;
  }

  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function projectRelativeMeters(origin, point) {
  const originLat = Number(origin?.lat);
  const originLng = Number(origin?.lng);
  const pointLat = Number(point?.lat);
  const pointLng = Number(point?.lng);

  if (
    !Number.isFinite(originLat) ||
    !Number.isFinite(originLng) ||
    !Number.isFinite(pointLat) ||
    !Number.isFinite(pointLng)
  ) {
    return null;
  }

  const earthRadiusMeters = 6371e3;
  const originLatRadians = toRadians(originLat);
  const pointLatRadians = toRadians(pointLat);
  const avgLatRadians = (originLatRadians + pointLatRadians) / 2;
  const dLat = pointLatRadians - originLatRadians;
  const dLng = toRadians(pointLng - originLng);

  return {
    xMeters: earthRadiusMeters * dLng * Math.cos(avgLatRadians),
    yMeters: earthRadiusMeters * dLat,
  };
}

export function buildRouteTracePoint(origin, point, totalDistanceMeters = 0) {
  const projected = projectRelativeMeters(origin, point);
  const pointTimestampMs = Number(point?.timestamp);
  const originTimestampMs = Number(origin?.timestamp);

  if (!projected) {
    return null;
  }

  return {
    xMeters: roundTo(projected.xMeters, 2),
    yMeters: roundTo(projected.yMeters, 2),
    tSeconds:
      Number.isFinite(pointTimestampMs) && Number.isFinite(originTimestampMs)
        ? roundTo(Math.max(0, (pointTimestampMs - originTimestampMs) / 1000), 1)
        : null,
    totalDistanceMeters: roundTo(totalDistanceMeters, 1),
  };
}

export function buildGeoRouteTracePoint(
  origin,
  point,
  totalDistanceMeters = 0,
) {
  const lat = Number(point?.lat);
  const lng = Number(point?.lng);
  const pointTimestampMs = Number(point?.timestamp);
  const originTimestampMs = Number(origin?.timestamp);
  const accuracyMeters = readFiniteNumber(point?.accuracy);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return {
    lat: roundTo(lat, 6),
    lng: roundTo(lng, 6),
    tSeconds:
      Number.isFinite(pointTimestampMs) && Number.isFinite(originTimestampMs)
        ? roundTo(Math.max(0, (pointTimestampMs - originTimestampMs) / 1000), 1)
        : null,
    totalDistanceMeters: roundTo(totalDistanceMeters, 1),
    accuracyMeters:
      accuracyMeters != null ? Math.max(0, roundTo(accuracyMeters, 1)) : null,
  };
}

export function buildGeoTrackingPoint(position) {
  const accuracy = Number(position?.coords?.accuracy);
  const latitude = Number(position?.coords?.latitude);
  const longitude = Number(position?.coords?.longitude);
  const timestamp = Number(position?.timestamp || Date.now());

  if (
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    (Number.isFinite(accuracy) && accuracy > MAX_ACCEPTED_ACCURACY_METERS)
  ) {
    return null;
  }

  return {
    lat: latitude,
    lng: longitude,
    timestamp,
    accuracy: Number.isFinite(accuracy) ? accuracy : null,
  };
}

function routeTraceDistanceMeters(a, b) {
  const x1 = Number(a?.xMeters);
  const y1 = Number(a?.yMeters);
  const x2 = Number(b?.xMeters);
  const y2 = Number(b?.yMeters);

  if (
    !Number.isFinite(x1) ||
    !Number.isFinite(y1) ||
    !Number.isFinite(x2) ||
    !Number.isFinite(y2)
  ) {
    return 0;
  }

  return Math.hypot(x2 - x1, y2 - y1);
}

function geoRouteTraceDistanceMeters(a, b) {
  return haversineDistanceMeters(a, b);
}

export function appendRouteTracePoint(
  routeTraceRef,
  nextPoint,
  { force = false } = {},
) {
  if (!nextPoint) {
    return;
  }

  const routeTrace = Array.isArray(routeTraceRef.current)
    ? routeTraceRef.current
    : [];

  if (!routeTrace.length) {
    routeTraceRef.current = [nextPoint];
    return;
  }

  const previousPoint = routeTrace[routeTrace.length - 1];
  const secondsDelta =
    readFiniteNumber(nextPoint?.tSeconds) != null &&
    readFiniteNumber(previousPoint?.tSeconds) != null
      ? nextPoint.tSeconds - previousPoint.tSeconds
      : null;
  const distanceDelta = routeTraceDistanceMeters(previousPoint, nextPoint);

  if (
    !force &&
    secondsDelta != null &&
    secondsDelta < ROUTE_TRACE_MIN_POINT_INTERVAL_MS / 1000 &&
    distanceDelta < ROUTE_TRACE_MIN_POINT_DISTANCE_METERS
  ) {
    return;
  }

  if (
    !force &&
    distanceDelta < 0.05 &&
    (secondsDelta == null || secondsDelta < 0.1)
  ) {
    return;
  }

  routeTraceRef.current = [...routeTrace, nextPoint];
}

export function appendGeoRouteTracePoint(
  geoRouteTraceRef,
  nextPoint,
  { force = false } = {},
) {
  if (!nextPoint) {
    return;
  }

  const geoRouteTrace = Array.isArray(geoRouteTraceRef.current)
    ? geoRouteTraceRef.current
    : [];

  if (!geoRouteTrace.length) {
    geoRouteTraceRef.current = [nextPoint];
    return;
  }

  const previousPoint = geoRouteTrace[geoRouteTrace.length - 1];
  const secondsDelta =
    readFiniteNumber(nextPoint?.tSeconds) != null &&
    readFiniteNumber(previousPoint?.tSeconds) != null
      ? nextPoint.tSeconds - previousPoint.tSeconds
      : null;
  const distanceDelta = geoRouteTraceDistanceMeters(previousPoint, nextPoint);

  if (
    !force &&
    secondsDelta != null &&
    secondsDelta < ROUTE_TRACE_MIN_POINT_INTERVAL_MS / 1000 &&
    distanceDelta < ROUTE_TRACE_MIN_POINT_DISTANCE_METERS
  ) {
    return;
  }

  if (
    !force &&
    distanceDelta < 0.05 &&
    (secondsDelta == null || secondsDelta < 0.1)
  ) {
    return;
  }

  geoRouteTraceRef.current = [...geoRouteTrace, nextPoint];
}

export function compressRouteTrace(
  points = [],
  maxPoints = ROUTE_TRACE_MAX_POINTS,
) {
  if (!Array.isArray(points) || points.length <= maxPoints) {
    return Array.isArray(points) ? points : [];
  }

  const keepCount = Math.max(2, Math.floor(maxPoints));
  const lastIndex = points.length - 1;
  const compacted = [];

  for (let slot = 0; slot < keepCount; slot += 1) {
    const index =
      slot === keepCount - 1
        ? lastIndex
        : Math.round((slot * lastIndex) / (keepCount - 1));
    const point = points[index];

    if (point && compacted[compacted.length - 1] !== point) {
      compacted.push(point);
    }
  }

  return compacted;
}

export function speakPrompt({ text, lang, audioMuted = false }) {
  if (
    audioMuted ||
    !text ||
    typeof window === "undefined" ||
    typeof window.SpeechSynthesisUtterance !== "function" ||
    !window.speechSynthesis
  ) {
    return;
  }

  try {
    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.lang = lang || "en-GB";
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  } catch {}
}
